import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { apiErrorMessage } from '@shared/api/client';
import { useCreateTransaction } from '@shared/hooks/use-transaction-actions';
import type { QuestionnaireQuestion, ServiceDetail, SubServiceSummary } from '@shared/types/api';
import { formatRupees } from '@/lib/format';

export function StartTransactionDialog({
  open,
  onClose,
  service,
}: {
  open: boolean;
  onClose: () => void;
  service: ServiceDetail;
}) {
  const navigate = useNavigate();
  const create = useCreateTransaction();
  const [agreed, setAgreed] = useState(false);
  const [subId, setSubId] = useState<string | undefined>(
    service.subServices.find((s) => s.isActive)?.id,
  );

  const subServices = service.subServices.filter((s) => s.isActive);

  // Sort the questionnaire by `order`, then split: OPTIONS and required TEXT
  // questions are answered upfront in this dialog. Non-required TEXT will
  // be asked by the system in the chat once the transaction opens (see
  // chat service's questionnaire handler).
  const orderedQuestions = useMemo(
    () => [...(service.questionnaire ?? [])].sort((a, b) => a.order - b.order),
    [service.questionnaire],
  );
  const upfrontQuestions = orderedQuestions.filter((q) => q.type === 'OPTIONS');
  const pendingChatQuestions = orderedQuestions.filter((q) => q.type === 'TEXT');

  const [answers, setAnswers] = useState<Record<string, string>>({});
  function setAnswer(id: string, value: string) {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  }

  // Block submit until every required OPTIONS question has an answer.
  const missingRequired = upfrontQuestions.some((q) => q.required && !(answers[q.id] ?? '').trim());

  function submit() {
    if (!agreed || missingRequired) return;
    // Send only answered questions. TEXT answers are intentionally omitted
    // here — the chat flow captures those as the user responds.
    create.mutate(
      {
        serviceId: service.id,
        subServiceId: subId,
        tncAccepted: true,
        questionnaireAnswers: Object.fromEntries(
          Object.entries(answers).filter(([, v]) => v.trim().length > 0),
        ),
      },
      {
        onSuccess: (tx) => {
          onClose();
          const chatPath = window.location.pathname.startsWith('/associate')
            ? '/associate/chat'
            : '/client/chat';
          navigate(`${chatPath}/${tx.id}`, { state: { showWelcome: true } });
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Start: ${service.name}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!agreed || missingRequired || create.isPending}>
            {create.isPending ? 'Creating…' : 'Start & Open Chat'}
          </Button>
        </>
      }
    >
      {subServices.length > 0 ? (
        <section className="mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Choose a sub-service
          </h3>
          <div className="mt-2 space-y-2">
            {subServices.map((ss) => (
              <SubServiceOption
                key={ss.id}
                sub={ss}
                selected={subId === ss.id}
                onSelect={() => setSubId(ss.id)}
              />
            ))}
          </div>
        </section>
      ) : null}

      {upfrontQuestions.length > 0 ? (
        <section className="mb-4 space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            A few quick questions
          </h3>
          {upfrontQuestions.map((q) => (
            <OptionsField
              key={q.id}
              question={q}
              value={answers[q.id] ?? ''}
              onChange={(v) => setAnswer(q.id, v)}
            />
          ))}
        </section>
      ) : null}

      {pendingChatQuestions.length > 0 ? (
        <p className="mb-4 rounded-md border border-brand-100 bg-brand-50/40 px-3 py-2 text-xs text-brand-800">
          {pendingChatQuestions.length === 1
            ? "We'll ask 1 more detail in the chat once we open it."
            : `We'll ask ${pendingChatQuestions.length} more details in the chat once we open it.`}
        </p>
      ) : null}

      <label className="mt-2 flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          I accept the{' '}
          <a href="/terms" target="_blank" className="underline underline-offset-2">
            Terms &amp; Conditions
          </a>{' '}
          and{' '}
          <a href="/privacy" target="_blank" className="underline underline-offset-2">
            Privacy Policy
          </a>
          . I understand an invoice will be issued for this service.
        </span>
      </label>

      {create.isError ? (
        <p className="mt-3 text-sm text-red-600">{apiErrorMessage(create.error)}</p>
      ) : null}
    </Dialog>
  );
}

function SubServiceOption({
  sub,
  selected,
  onSelect,
}: {
  sub: SubServiceSummary;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-start justify-between rounded-md border p-3 text-left ${
        selected ? 'border-brand-600 bg-brand-50/40' : 'border-gray-200 bg-white hover:bg-gray-50'
      }`}
    >
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-900">{sub.name}</div>
        {sub.subCategory ? <div className="text-xs text-gray-500">{sub.subCategory}</div> : null}
      </div>
      {sub.defaultServiceChargeRupees ? (
        <div className="text-xs font-medium text-brand-700">
          {formatRupees(sub.defaultServiceChargeRupees)}
        </div>
      ) : null}
    </button>
  );
}

// A radio group for ≤4 options; <select> for more. Keeps the dialog short
// when admins define long option lists.
function OptionsField({
  question,
  value,
  onChange,
}: {
  question: QuestionnaireQuestion;
  value: string;
  onChange: (v: string) => void;
}) {
  const useSelect = question.options.length > 4;
  return (
    <div>
      <label className="block text-sm font-medium text-gray-800">
        {question.label}
        {question.required ? <span className="ml-0.5 text-red-500">*</span> : null}
      </label>
      {useSelect ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1.5 h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
        >
          <option value="">Select…</option>
          {question.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {question.options.map((opt) => {
            const selected = value === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange(opt)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  selected
                    ? 'border-brand-700 bg-brand-700 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
